import { useFormContext } from 'react-hook-form';

import { VehicleTypeStep } from './VehicleTypeStep';
import { RegistrationNumberStep } from './RegistrationNumberStep';
import { PriorPolicyStep } from './PriorPolicyStep';
import { MakeStep } from './MakeStep';
import { ModelStep } from './ModelStep';
import { VariantStep } from './VariantStep';
import { RegistrationDateStep } from './RegistrationDateStep';
import { ManufacturingYearStep } from './ManufacturingYearStep';
import { RTOStep } from './RTOStep';
import type { MotorQuoteFormData } from '../../schema';

/**
 * Everything about the vehicle, on one screen.
 *
 * These were nine consecutive steps. They are the same nine components,
 * unchanged and still individually responsible for their own field, cascade
 * resets and validation — this only decides where they sit. Grouping them is
 * what makes the registration lookup visible: RegistrationNumberStep prefills
 * Make/Model/Variant/dates/RTO, and when those live on the same screen the
 * broker watches it happen and corrects it in place, instead of pressing Next
 * through six screens that were silently filled in behind them.
 *
 * Each child still renders its own `<h2>`; the wrapper below demotes those to
 * section-heading size so the page reads as one form with labelled sections
 * rather than nine stacked pages.
 */

/**
 * Demotes each child step's own `<h2>` + description to section chrome.
 * The descriptions are kept — some of them carry real instruction, like how to
 * scan a number plate — but pulled in tight so nine of them do not read as
 * nine separate pages.
 */
const SECTION =
  '[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:uppercase [&_h2]:tracking-wide ' +
  '[&_h2]:text-gray-500 dark:[&_h2]:text-gray-400 ' +
  '[&_h2+p]:mt-0.5 [&_h2+p]:mb-3 [&_h2+p]:text-xs';

export function VehicleDetailsStep() {
  const { watch } = useFormContext<MotorQuoteFormData>();

  // Prior policy is wanted for Renewal/Rollover only. This used to be done by
  // dropping a whole step (motorStepsForBusinessType); now the section simply
  // does not render, and motorStepFieldsFor drops its fields from the gate to
  // match. Both halves key off the same condition.
  const businessType = watch('business_type');
  const showPriorPolicy = !!businessType && businessType !== 'new';

  return (
    <div className={`space-y-8 ${SECTION}`}>
      <VehicleTypeStep />

      <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
        <RegistrationNumberStep />
      </div>

      {showPriorPolicy && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
          <PriorPolicyStep />
        </div>
      )}

      {/* Make -> Model -> Variant cascade. Left to right in the order they
          unlock each other, so a reset further up is visible immediately. */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <MakeStep />
        <ModelStep />
        <VariantStep />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <RegistrationDateStep />
        <ManufacturingYearStep />
        <RTOStep />
      </div>
    </div>
  );
}
